/** The persistent context bar (U001, U003): environment, mode, account, exchange date, strategy, connection, entry block. */
import type { Environment, Freshness, Mode } from "../contracts/envelope.ts";
import type { AccountRow } from "../contracts/views.ts";
import { h, setChildren } from "./dom.ts";
import { formatDurationUs, formatExchangeDate, formatInstant } from "./format.ts";
import type { ConnectionState } from "./stream.ts";
import type { Support } from "./support.ts";

export interface ContextInfo {
  readonly environment: Environment | null;
  readonly mode: Mode | null;
  readonly accountId: string | null;
  readonly accounts: readonly AccountRow[];
  readonly exchangeDate: string | null;
  readonly strategyVersion: string | null;
  readonly connection: ConnectionState;
  readonly freshness: Freshness | null;
  readonly ageUs: number | null;
  readonly lastSync: string | null;
  readonly entryBlocks: readonly { readonly reason: string; readonly scope: string }[];
  readonly support: Support;
}

export interface ContextBar {
  readonly element: HTMLElement;
  update(info: ContextInfo): void;
}

export interface BarHandlers {
  readonly onAccount: (accountId: string) => void;
  readonly onLogout: () => void;
}

/** Describe a connection state in words (never colour alone). */
export function connectionText(state: ConnectionState): string {
  switch (state.kind) {
    case "idle":
      return "Not connected";
    case "connecting":
      return "Connecting";
    case "live":
      return "Connected (live)";
    case "reconciling":
      return `Disconnected: reconciling after ${state.cause} (attempt ${String(state.attempt + 1)})`;
    case "refused":
      return `Stream refused: ${state.reason}`;
  }
}

const UNKNOWN = "unknown";

/** Build the bar; `update` rewrites text in place so focus and the account select survive live updates. */
export function createContextBar(handlers: BarHandlers): ContextBar {
  const slot = (name: string, label: string): { box: HTMLElement; value: HTMLElement } => {
    const value = h("span", { class: "ctx-value", attrs: { "data-ctx": name } });
    return {
      box: h(
        "div",
        { class: `ctx ctx-${name}` },
        h("span", { class: "ctx-label" }, `${label} `),
        value,
      ),
      value,
    };
  };
  const environment = slot("environment", "Environment");
  const mode = slot("mode", "Mode");
  const exchange = slot("exchange-date", "Exchange date");
  const strategy = slot("strategy", "Strategy");
  const connection = slot("connection", "Connection");
  const sync = slot("sync", "Last sync");
  const select = h("select", {
    attrs: { "aria-label": "Selected account", name: "account" },
    on: {
      change: (event) => {
        handlers.onAccount((event.target as HTMLSelectElement).value);
      },
    },
  });
  const announce = h("p", {
    class: "announce",
    attrs: { role: "status", "aria-live": "assertive" },
  });
  const blocks = h("div", { class: "entry-blocks", attrs: { "data-entry-blocks": "" } });
  const unsupported = h("div", { class: "unsupported-slot" });
  const logout = h(
    "button",
    { attrs: { type: "button" }, on: { click: handlers.onLogout } },
    "Log out",
  );
  const element = h(
    "header",
    { class: "context-bar", attrs: { role: "banner", "aria-label": "Context" } },
    h(
      "div",
      { class: "ctx-row" },
      environment.box,
      mode.box,
      h("div", { class: "ctx" }, h("label", {}, "Account ", select)),
      exchange.box,
      strategy.box,
      connection.box,
      sync.box,
      logout,
    ),
    announce,
    blocks,
    unsupported,
  );
  let previous = "";
  let optionsKey = "";
  return {
    element,
    update: (info) => {
      environment.value.textContent = info.environment ?? UNKNOWN;
      environment.value.dataset.environment = info.environment ?? UNKNOWN;
      mode.value.textContent = info.mode ?? UNKNOWN;
      mode.value.dataset.mode = info.mode ?? UNKNOWN;
      exchange.value.textContent =
        info.exchangeDate === null ? UNKNOWN : formatExchangeDate(info.exchangeDate);
      strategy.value.textContent = info.strategyVersion ?? UNKNOWN;
      connection.value.textContent = connectionText(info.connection);
      connection.value.dataset.connection = info.connection.kind;
      const age = info.ageUs === null ? "" : `, data age ${formatDurationUs(info.ageUs)}`;
      sync.value.textContent =
        info.lastSync === null
          ? "none yet"
          : `${formatInstant(info.lastSync)}${age} (${info.freshness ?? UNKNOWN})`;
      const key = info.accounts.map((account) => account.accountId).join("|");
      if (key !== optionsKey) {
        optionsKey = key;
        setChildren(
          select,
          info.accounts.map((account) =>
            h(
              "option",
              { attrs: { value: account.accountId } },
              `${account.label} (${account.accountId})`,
            ),
          ),
        );
      }
      select.value = info.accountId ?? "";
      const now = `${info.accountId ?? UNKNOWN}|${info.mode ?? UNKNOWN}`;
      if (previous !== "" && previous !== now) {
        announce.textContent = `Context changed: account ${info.accountId ?? UNKNOWN}, mode ${info.mode ?? UNKNOWN}.`;
      }
      previous = now;
      setChildren(
        blocks,
        info.entryBlocks.length === 0
          ? []
          : [
              h(
                "div",
                { class: "entry-block", attrs: { role: "alert" } },
                h("strong", {}, "New entries are blocked. "),
                info.entryBlocks
                  .map((block) => `${block.reason} (scope ${block.scope})`)
                  .join("; "),
              ),
            ],
      );
      setChildren(unsupported, []);
      if (!info.support.supported) {
        unsupported.append(
          h(
            "div",
            {
              class: "state-banner state-unsupported",
              attrs: { role: "alert", "data-state": "unsupported" },
            },
            `Unsupported browser (${info.support.reason ?? UNKNOWN}): read-only; every command control is disabled.`,
          ),
        );
      }
    },
  };
}
